import { getAddress } from "viem";
import memoize from "lodash/memoize";
import { ChainId, Token, Currency } from "@donaswap/sdk";

const mapping: { [key: number]: string } = {
  [ChainId.FIRECHAIN]: "firechain",
  [ChainId.BSC]: "smartchain",
  [ChainId.ETHEREUM]: "ethereum",
  [ChainId.POLYGON_ZKEVM]: "polygonzkevm",
  [ChainId.ARBITRUM_ONE]: "arbitrum",
  [ChainId.POLYGON]: "matic",
  [ChainId.CRO]: "cronos",
  [ChainId.AVALANCHE]: 'avalanche',
  [ChainId.FANTOM_OPERA]: 'fantom',
  [ChainId.CELO]: 'celo',
  [ChainId.OPTIMISM]: 'optimism',
  [ChainId.SHIBARIUM]: 'shibarium',
  [ChainId.PULSECHAIN]: 'pulsechain',
  [ChainId.XEN]: 'xen',
  [ChainId.HARMONY]: 'harmony',
  [ChainId.COINEX]: 'coinex',
  [ChainId.DOGECHAIN]: 'dogechain',
  [ChainId.ETHEREUM_CLASSIC]: 'ethereumClassic',
  [ChainId.FUSION]: 'fusion',
  [ChainId.HECO]: 'heco',
  [ChainId.KCC]: 'kuchain',
  [ChainId.KLAYTN]: 'klaytn',
  [ChainId.OKXCHAIN]: 'okchain',
  [ChainId.THETHA]: 'theta',
  [ChainId.ULTRON]: 'ultron',
  [ChainId.MOONBEAM]: 'moonbeam',
  [ChainId.MOONRIVER]: 'moonriver',
  [ChainId.AURORA]: 'aurora',
  [ChainId.BOBA]: 'boba',
  [ChainId.GNOSIS]: 'gnosis',
  [ChainId.METIS]: 'metis',
  [ChainId.TELOS]: 'telos',
  [ChainId.SYSCOIN]: 'syscoin',
  [ChainId.IOTEX]: 'iotex',
  [ChainId.SHIDEN]: 'shiden',
  [ChainId.ASTAR]: 'astar',
  [ChainId.FUSE]: 'fuse',
  [ChainId.VELAS]: 'velas',
  [ChainId.REI]: 'rei',
  [ChainId.KEKCHAIN]: 'kekchain',
  [ChainId.TOMOCHAIN]: 'tomochain',
  [ChainId.THUNDERCORE]: 'thundercore',
  [ChainId.WANCHAIN]: 'wanchain',
  [ChainId.RSK]: 'rootstock',
  [ChainId.ELASTOS]: 'elastos',
  [ChainId.CONFLUX]: 'conflux',
  [ChainId.BRISECHAIN]: 'brisechain',
  [ChainId.MUUCHAIN]: 'muuchain',
  [ChainId.CANTO]: 'canto',
  [ChainId.OASIS_EMERALD]: 'oasisEmerald',
  [ChainId.VISION]: 'vision',
  [ChainId.STEP]: 'step',
  [ChainId.METER]: 'meter',
  [ChainId.GODWOKEN]: 'godwoken',
  [ChainId.CALLISTO]: 'callisto',
  [ChainId.EVMOS]: 'evmos',
  [ChainId.ENERGY_WEB_CHAIN]: 'energyWebchain',
  [ChainId.BASE]: 'base',
  [ChainId.KAVA]: 'kava',
  [ChainId.CLOVER]: 'clover',
  [ChainId.DEFICHAIN]: 'defichain',
  [ChainId.BRONOS]: 'bronos',
  [ChainId.FILECOIN]: 'filecoin',
  [ChainId.FLARE]: 'flare',
  [ChainId.TARAXA]: 'taraxa',
};

export const getTokenLogoURL = memoize(
  (token?: Token) => {
    if (token && mapping[token.chainId]) {
      return `https://assets-cdn.trustwallet.com/blockchains/${mapping[token.chainId]}/assets/${getAddress(
        token.address
      )}/logo.png`;
    }
    return null;
  },
  (t) => `${t?.chainId}#${t?.address}`
);

export const getTokenLogoURLByAddress = memoize(
  (address?: string, chainId?: number) => {
    if (address && chainId && mapping[chainId]) {
      return `https://assets-cdn.trustwallet.com/blockchains/${mapping[chainId]}/assets/${getAddress(
        address
      )}/logo.png`;
    }
    return null;
  },
  (address, chainId) => `${chainId}#${address}`
);

const chainName: { [key: number]: string } = {
  [ChainId.FIRECHAIN]: "fire",
  [ChainId.BSC]: "",
  [ChainId.ETHEREUM]: "eth",
  [ChainId.POLYGON_ZKEVM]: "polygonzkevm",
  [ChainId.ARBITRUM_ONE]: "arb",
  [ChainId.POLYGON]: "matic",
  [ChainId.CRO]: "cronos",
  [ChainId.AVALANCHE]: 'avalanche',
  [ChainId.FANTOM_OPERA]: 'fantom',
  [ChainId.CELO]: 'celo',
  [ChainId.OPTIMISM]: 'optimism',
  [ChainId.SHIBARIUM]: 'shibarium',
  [ChainId.PULSECHAIN]: 'pulsechain',
  [ChainId.XEN]: 'xen',
  [ChainId.HARMONY]: 'harmony',
  [ChainId.COINEX]: 'coinex',
  [ChainId.DOGECHAIN]: 'dogechain',
  [ChainId.ETHEREUM_CLASSIC]: 'ethereumClassic',
  [ChainId.FUSION]: 'fusion',
  [ChainId.HECO]: 'heco',
  [ChainId.KCC]: 'kuchain',
  [ChainId.KLAYTN]: 'klaytn',
  [ChainId.OKXCHAIN]: 'okchain',
  [ChainId.THETHA]: 'theta',
  [ChainId.ULTRON]: 'ultron',
  [ChainId.MOONBEAM]: 'moonbeam',
  [ChainId.MOONRIVER]: 'moonriver',
  [ChainId.AURORA]: 'aurora',
  [ChainId.BOBA]: 'boba',
  [ChainId.GNOSIS]: 'gnosis',
  [ChainId.METIS]: 'metis',
  [ChainId.TELOS]: 'telos',
  [ChainId.SYSCOIN]: 'syscoin',
  [ChainId.IOTEX]: 'iotex',
  [ChainId.SHIDEN]: 'shiden',
  [ChainId.ASTAR]: 'astar',
  [ChainId.FUSE]: 'fuse',
  [ChainId.VELAS]: 'velas',
  [ChainId.REI]: 'rei',
  [ChainId.KEKCHAIN]: 'kekchain',
  [ChainId.TOMOCHAIN]: 'tomochain',
  [ChainId.THUNDERCORE]: 'thundercore',
  [ChainId.WANCHAIN]: 'wanchain',
  [ChainId.RSK]: 'rootstock',
  [ChainId.ELASTOS]: 'elastos',
  [ChainId.CONFLUX]: 'conflux',
  [ChainId.BRISECHAIN]: 'brisechain',
  [ChainId.MUUCHAIN]: 'muuchain',
  [ChainId.CANTO]: 'canto',
  [ChainId.OASIS_EMERALD]: 'oasisEmerald',
  [ChainId.VISION]: 'vision',
  [ChainId.STEP]: 'step',
  [ChainId.METER]: 'meter',
  [ChainId.GODWOKEN]: 'godwoken',
  [ChainId.CALLISTO]: 'callisto',
  [ChainId.EVMOS]: 'evmos',
  [ChainId.ENERGY_WEB_CHAIN]: 'energyWebchain',
  [ChainId.BASE]: 'base',
  [ChainId.KAVA]: 'kava',
  [ChainId.CLOVER]: 'clover',
  [ChainId.DEFICHAIN]: 'defichain',
  [ChainId.BRONOS]: 'bronos',
  [ChainId.FILECOIN]: 'filecoin',
  [ChainId.FLARE]: 'flare',
  [ChainId.TARAXA]: 'taraxa',
};

export const getCurrencyLogoUrls = memoize(
  (currency?: Currency): string[] => {
    const chainId = currency?.chainId || ChainId.BSC;
    const tokenAddress = getAddress(currency?.wrapped?.address || "");
    const trustWalletLogo = getTokenLogoURL(currency?.wrapped);
    const logoUrl = `https://tokens.pancakeswap.finance/images/${
      chainId === ChainId.BSC ? "" : `${chainName[chainId]}/`
    }${tokenAddress}.png`;
    return [trustWalletLogo, logoUrl].filter((url) => Boolean(url)) as string[];
  },
  (currency?: Currency) => `logoUrls#${currency?.chainId}#${currency?.wrapped?.address}`
);
